import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { Check, Plus, Minus, ArrowRight, Sparkles, Flower, Package } from "lucide-react";

const WRAP_FEE = 149;

export default function BouquetBuilder() {
  const [candles, setCandles] = useState([]);
  const [planters, setPlanters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // { productId: { product, quantity } }
  const [planterId, setPlanterId] = useState(null); // optional planter
  const [name, setName] = useState("Custom");
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/products?category=candles"),
      api.get("/products?category=garden-decor-accessories"),
    ]).then(([c, p]) => {
      setCandles(c.data);
      setPlanters(p.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const chosenPlanter = planters.find((p) => p.id === planterId) || null;

  const summary = useMemo(() => {
    const rows = Object.values(selected);
    const count = rows.reduce((s, r) => s + r.quantity, 0);
    const candlesTotal = rows.reduce((s, r) => s + r.product.price * r.quantity, 0);
    const planterPrice = chosenPlanter ? chosenPlanter.price : 0;
    const total = candlesTotal + planterPrice + (count > 0 ? WRAP_FEE : 0);
    return { rows, count, candlesTotal, planterPrice, total };
  }, [selected, chosenPlanter]);

  const setQty = (product, delta) => {
    setSelected((prev) => {
      const cur = prev[product.id]?.quantity || 0;
      const next = Math.max(0, Math.min(10, cur + delta));
      const copy = { ...prev };
      if (next === 0) delete copy[product.id];
      else copy[product.id] = { product, quantity: next };
      return copy;
    });
  };

  const addBouquetToCart = () => {
    if (summary.count < 3) {
      toast.error("Pick at least 3 candles to build a bouquet");
      return;
    }
    const details = summary.rows.map((r) => `${r.product.name} × ${r.quantity}`);
    if (chosenPlanter) details.push(`+ Planter: ${chosenPlanter.name}`);
    details.push(`+ Hand-wrap & ribbon (₹${WRAP_FEE})`);
    const firstImage = chosenPlanter?.images?.[0] || summary.rows[0]?.product.images?.[0];
    const uniqueId = `custom-bouquet-${Date.now()}`;
    const label = chosenPlanter
      ? `${name} Bouquet — ${summary.count} candles + planter`
      : `${name} Bouquet — ${summary.count} candles`;
    addItem(
      { id: uniqueId, slug: uniqueId, name: label, price: summary.total, images: [firstImage] },
      1,
      { details }
    );
    toast.success("Your custom bouquet was added to cart");
    navigate("/cart");
  };

  return (
    <div data-testid="bouquet-builder-page">
      <section className="border-b border-[#E3DCD2] bg-gradient-to-b from-[#F3EFEA]/60 to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3 flex items-center gap-2">
                <Flower className="w-3.5 h-3.5" strokeWidth={1.5} /> ✦ Build your own bouquet
              </div>
              <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95]">
                Design a bouquet
                <br /><em className="text-[#D27D60]">just for them.</em>
              </h1>
              <p className="text-base text-[#4a4a4a] mt-5 max-w-xl leading-relaxed">
                Pick your favourite candles — add a planter if you'd like — and we'll hand-arrange them into a wrapped, ribbon-tied bouquet ready to gift. Wrap & binding: <span className="font-medium text-[#1A1A1A]">₹{WRAP_FEE}</span>.
              </p>
            </div>
            <div className="md:col-span-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">Bouquet name (optional)</div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value || "Custom")}
                  placeholder="For Anaya, With Love"
                  className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors font-serif-display text-xl"
                  data-testid="bouquet-name-input"
                />
              </label>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-14">
          {/* Step 1 · Candles */}
          <div>
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-widest-plus text-[#D27D60] mb-1">Step 1</div>
                <h2 className="font-serif-display text-3xl">Choose your candles</h2>
              </div>
              <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">
                {summary.count === 0 ? "Pick 3 or more" : `${summary.count} selected`}
              </div>
            </div>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[0,1,2,3,4,5].map(i => <div key={i} className="animate-pulse aspect-[4/5] bg-[#F3EFEA] rounded-md" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {candles.map((p) => {
                  const qty = selected[p.id]?.quantity || 0;
                  const picked = qty > 0;
                  return (
                    <div
                      key={p.id}
                      className={`relative rounded-md overflow-hidden transition-all ${
                        picked ? "ring-2 ring-[#D27D60] shadow-[0_10px_30px_rgba(210,125,96,0.2)]" : "ring-1 ring-[#E3DCD2] hover:ring-[#9BA595]"
                      }`}
                      data-testid={`bouquet-candle-${p.slug}`}
                    >
                      <div className="aspect-[4/5] bg-[#F3EFEA] relative">
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                        {picked && (
                          <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-[#D27D60] text-[#FDFBF7] flex items-center justify-center">
                            <Check className="w-4 h-4" strokeWidth={2} />
                          </div>
                        )}
                      </div>
                      <div className="p-3 bg-[#FDFBF7]">
                        <div className="font-medium text-sm leading-tight line-clamp-2">{p.name}</div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="text-sm font-medium">₹{p.price}</div>
                          {picked ? (
                            <div className="flex items-center gap-2 border border-[#E3DCD2] rounded-full">
                              <button onClick={() => setQty(p, -1)} className="p-1.5 hover:text-[#D27D60] transition-colors" data-testid={`bouquet-dec-${p.slug}`}>
                                <Minus className="w-3 h-3" strokeWidth={1.5} />
                              </button>
                              <span className="text-xs min-w-[1rem] text-center">{qty}</span>
                              <button onClick={() => setQty(p, +1)} className="p-1.5 hover:text-[#D27D60] transition-colors" data-testid={`bouquet-inc-${p.slug}`}>
                                <Plus className="w-3 h-3" strokeWidth={1.5} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setQty(p, +1)}
                              className="text-[10px] uppercase tracking-widest-plus px-3 py-1.5 rounded-full bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#D27D60] transition-colors"
                              data-testid={`bouquet-add-${p.slug}`}
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 2 · Planter (optional) */}
          {planters.length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest-plus text-[#D27D60] mb-1 flex items-center gap-1.5">
                    <Package className="w-3 h-3" strokeWidth={1.5} /> Step 2 · Optional
                  </div>
                  <h2 className="font-serif-display text-3xl">Add a planter</h2>
                  <p className="text-sm text-[#9BA595] mt-1">Elevate your bouquet with a ceramic tote or aviary base.</p>
                </div>
                {planterId && (
                  <button
                    onClick={() => setPlanterId(null)}
                    className="text-[10px] uppercase tracking-widest-plus underline underline-offset-4 hover:text-[#D27D60] transition-colors"
                    data-testid="bouquet-planter-clear"
                  >
                    Remove planter
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {planters.map((p) => {
                  const picked = planterId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPlanterId(picked ? null : p.id)}
                      className={`text-left rounded-md overflow-hidden transition-all ${
                        picked ? "ring-2 ring-[#D27D60] shadow-[0_10px_30px_rgba(210,125,96,0.2)]" : "ring-1 ring-[#E3DCD2] hover:ring-[#9BA595]"
                      }`}
                      data-testid={`bouquet-planter-${p.slug}`}
                    >
                      <div className="aspect-[4/5] bg-[#F3EFEA] relative">
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                        {picked && (
                          <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-[#D27D60] text-[#FDFBF7] flex items-center justify-center">
                            <Check className="w-4 h-4" strokeWidth={2} />
                          </div>
                        )}
                      </div>
                      <div className="p-3 bg-[#FDFBF7]">
                        <div className="font-medium text-sm leading-tight line-clamp-2">{p.name}</div>
                        <div className="flex items-center justify-between mt-2 text-xs">
                          <span className="font-medium text-sm">₹{p.price}</span>
                          <span className={`uppercase tracking-widest-plus text-[10px] ${picked ? "text-[#D27D60]" : "text-[#9BA595]"}`}>
                            {picked ? "Selected" : "Tap to add"}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-4">
          <div className="p-8 bg-[#F3EFEA]/60 rounded-lg sticky top-32" data-testid="bouquet-summary">
            <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-3">Your bouquet</div>
            <h3 className="font-serif-display text-3xl leading-tight mb-6">
              {name} Bouquet
              {summary.count > 0 && (<><br /><span className="text-[#9BA595] text-xl">
                {summary.count} candle{summary.count !== 1 ? "s" : ""}
                {chosenPlanter && " + planter"}
              </span></>)}
            </h3>

            {summary.rows.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-[#E3DCD2] rounded-md">
                <Sparkles className="w-6 h-6 mx-auto text-[#9BA595] mb-2" strokeWidth={1.5} />
                <div className="text-sm text-[#9BA595]">Your picks will appear here</div>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  {summary.rows.map((r) => (
                    <div key={r.product.id} className="flex items-center gap-3 text-sm" data-testid={`bouquet-picked-${r.product.slug}`}>
                      <img src={r.product.images[0]} alt={r.product.name} className="w-10 h-10 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-xs">{r.product.name}</div>
                        <div className="text-[10px] text-[#9BA595]">₹{r.product.price} × {r.quantity}</div>
                      </div>
                      <div className="text-xs font-medium">₹{r.product.price * r.quantity}</div>
                    </div>
                  ))}
                  {chosenPlanter && (
                    <div className="flex items-center gap-3 text-sm pt-3 border-t border-[#E3DCD2]" data-testid="bouquet-picked-planter">
                      <img src={chosenPlanter.images[0]} alt={chosenPlanter.name} className="w-10 h-10 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-xs">{chosenPlanter.name}</div>
                        <div className="text-[10px] text-[#D27D60] uppercase tracking-widest-plus">Planter add-on</div>
                      </div>
                      <div className="text-xs font-medium">₹{chosenPlanter.price}</div>
                    </div>
                  )}
                </div>
                <div className="border-t border-[#E3DCD2] pt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Candles</span><span>₹{summary.candlesTotal}</span></div>
                  {chosenPlanter && <div className="flex justify-between"><span>Planter</span><span>₹{summary.planterPrice}</span></div>}
                  <div className="flex justify-between"><span>Bouquet wrap & binding</span><span>₹{WRAP_FEE}</span></div>
                </div>
                <div className="flex justify-between items-baseline pt-4 mt-4 border-t border-[#E3DCD2] mb-6">
                  <span className="text-sm uppercase tracking-widest-plus">Total</span>
                  <span className="font-serif-display text-3xl" data-testid="bouquet-total">₹{summary.total}</span>
                </div>
              </>
            )}

            <button
              onClick={addBouquetToCart}
              disabled={summary.count < 3}
              className="w-full bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3.5 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bouquet-add-to-cart-btn"
            >
              {summary.count < 3 ? `Pick ${3 - summary.count} more` : "Add bouquet to cart"}
              {summary.count >= 3 && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
            </button>
            <p className="mt-3 text-[10px] uppercase tracking-widest-plus text-center text-[#9BA595]">
              Minimum 3 candles · wrapped by hand
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
